// A drag on the electrical schematic's view (docs/claude/elecSchematic.md): what
// a press on what it hit starts, what each move makes, what the drop makes, and
// what is shown on the way — the preview asking the same refusal the drop does.
// Pure (no DOM): the app keeps the pointer events, the gesture and the undo step.

import type {
  ElecCellId,
  ElecRowId,
  ElecView,
  Vec2,
} from '../shared/types';
import {
  compareElecBoxesAuto,
  type ElecModel,
} from './elecSchematic';
import type {
  ElecHit,
  ElecLayout,
  ElecLayoutBox,
  ElecPick,
  Rect,
} from './elecLayout';
import type {
  ElecEdit,
  ElecRefusal,
} from './elecEdits';
import type { ElecDrawState } from './elecDraw';
import { unreachable } from '../shared/exhaustive';

/** A press on a view being dragged: where it was grabbed, where the pointer is,
 *  whether it has moved past the slop yet. */
export type ElecDrag = { grab: Vec2; cur: Vec2; moved: boolean } & (
  | { mode: 'aside'; which: 'legend' | 'table'; from: Vec2 }
  | { mode: 'border'; row: ElecRowId; after: string; dx0: number }
  | { mode: 'box'; key: string }
  | { mode: 'end'; key: string; box: string; fixed: Vec2; color: string }
  | { mode: 'jog'; key: string; P: Vec2; Q: Vec2 }
  | { mode: 'view'; v0: ElecView }
);

/** Whether a drag's moves are its edit, live in the gesture its first move
 *  begins; else it makes one edit at the drop, one undo step. */
const LIVE: Record<ElecDrag['mode'], boolean> = { aside: true, border: true, box: false, end: false, jog: true, view: true };
export const elecDragLive = (d: ElecDrag): boolean => LIVE[d.mode];

/** The box under a paper point, topmost first. */
export const elecBoxUnder = (layout: ElecLayout, p: Vec2): ElecLayoutBox | undefined =>
  [...layout.boxes].reverse().find((b) => p.x >= b.rect.x && p.x <= b.rect.x + b.rect.w && p.y >= b.rect.y && p.y <= b.rect.y + b.rect.h);
/** The middle of a box's top or bottom edge, whichever faces `p`: where a wire
 *  to it lands. */
export function elecFacing(r: Rect, p: Vec2): Vec2 {
  return { x: r.x + r.w / 2, y: p.y >= r.y + r.h / 2 ? r.y + r.h : r.y };
}

/** Where a box dropped (or added) at `at` goes (decision 15), so that it is
 *  then DRAWN there: the cell under it, after the boxes this view draws left of
 *  it and before those right of it — on the box's own line (a cell's slots and
 *  its bars stand apart; a bar: a junction box, or one already on its row's
 *  bars) —, as an index among ALL the cell's boxes, those this view leaves out
 *  included; right of them all, after the last. The boxes are counted in the
 *  model's order, which its lines are drawn in — but the client row's, which
 *  stands under what each is wired to: there in the order this view draws them
 *  (`drawn`, given when it is not the model's), with the view's own order of
 *  it (`auto`: `ElecLayout.clientAuto`) for the edit. A centred bar stands where its
 *  wires put it: the index is one fewest boxes end up on the wrong side of —
 *  the box's own place while that is one (a nudge is no move), else one that
 *  leaves the cell in its automatic order (no hand order written), else the
 *  last. */
export function elecDropTarget(layout: ElecLayout, model: ElecModel, key: string, at: Vec2): { cell: ElecCellId; index: number; rect: Rect; drawn?: string[]; auto?: string[] } | null {
  const cell = layout.cells.find((c) => at.x >= c.rect.x && at.x <= c.rect.x + c.rect.w && at.y >= c.rect.y && at.y <= c.rect.y + c.rect.h);
  if (!cell) return null;
  const inCell = model.boxes.filter((b) => b.cell === cell.id).map((b) => b.key);
  const shown = new Map(layout.boxes.filter((b) => b.box.cell === cell.id).map((b) => [b.key, b]));
  const mid = (k: string): number => { const r = shown.get(k)!.rect; return r.x + r.w / 2; };
  const client = cell.row === 'client';
  let drawn = inCell;
  if (client) {
    // Left to right as drawn; a box this view leaves out right after the box
    // before it in the model's order.
    drawn = inCell.filter((k) => shown.has(k)).sort((a, b) => mid(a) - mid(b));
    let prev: string | null = null;
    for (const k of inCell) { if (!shown.has(k)) drawn.splice(prev == null ? 0 : drawn.indexOf(prev) + 1, 0, k); prev = k; }
  }
  const rest = drawn.filter((k) => k !== key);
  // Its line in the cell: the one it is on, in its own row; else a junction box's bars.
  const self = layout.boxes.find((b) => b.key === key);
  const bar = !client && (self && self.box.row === cell.row ? self.line % 2 === 1 : !!model.boxByKey.get(key)?.junction);
  const mates = rest.filter((k) => shown.has(k) && (client || (shown.get(k)!.line % 2 === 1) === bar));
  const wrong = (i: number): number => mates.filter((k) => (mid(k) <= at.x) === (rest.indexOf(k) >= i)).length;
  const here = shown.has(key) ? drawn.indexOf(key) : -1;
  const fewest = Math.min(...rest.map((_, i) => wrong(i)), wrong(rest.length));
  const best = [...Array(rest.length + 1).keys()].filter((i) => wrong(i) === fewest);
  const me = model.boxByKey.get(key);
  const auto = (i: number): boolean => {
    const list = [...rest.slice(0, i), key, ...rest.slice(i)].map((k) => model.boxByKey.get(k));
    return list.every((b, j) => !!b && (j === 0 || compareElecBoxesAuto(list[j - 1]!, b) < 0));
  };
  const index = best.includes(here) ? here : (!client && me ? best.find(auto) : undefined) ?? best[best.length - 1];
  const same = drawn.every((k, i) => k === inCell[i]);
  return { cell: cell.id, index, rect: cell.rect, ...(same ? {} : { drawn }), ...(client ? { auto: layout.clientAuto } : {}) };
}

/** The drag a press on `hit` at `at` starts: a box to another slot or cell, a
 *  wire's end onto another box, a jog (only one between its ends) or a border
 *  sliding, the table or legend moving; anything else moves the whole view. */
export function elecDragStart(view: ElecView, layout: ElecLayout, model: ElecModel, hit: ElecHit, at: Vec2): ElecDrag {
  const base = { grab: at, cur: at, moved: false };
  const whole = (): ElecDrag => ({ ...base, mode: 'view', v0: structuredClone(view) });
  switch (hit.kind) {
    case 'table': case 'legend': {
      const r = (hit.kind === 'table' ? layout.table : layout.legend)!.rect;
      return { ...base, mode: 'aside', which: hit.kind, from: { x: r.x, y: r.y } };
    }
    case 'box': return { ...base, mode: 'box', key: hit.key };
    case 'end': {
      // From its other end — or, that one on another sheet, the grid's side.
      const w = layout.wires.find((x) => x.key === hit.key);
      const fixed = w?.ends.find((e) => e.box !== hit.box)?.at ?? w?.offSheet?.at;
      if (!w || !fixed) return whole();
      return { ...base, mode: 'end', key: hit.key, box: hit.box, fixed, color: w.color };
    }
    case 'jog': {
      const w = layout.wires.find((x) => x.key === hit.key);
      if (!w || w.offSheet || !w.jog) return whole();
      const P = w.points[0], Q = w.points[w.points.length - 1];
      // Only a wire that jogs between its ends (not one turning under both).
      if (!(w.jog.y > Math.min(P.y, Q.y) && w.jog.y < Math.max(P.y, Q.y))) return whole();
      return { ...base, mode: 'jog', key: hit.key, P, Q };
    }
    case 'border': {
      const dx0 = model.borders.find((b) => b.row === hit.row && b.after === hit.after)?.dx ?? 0;
      return { ...base, mode: 'border', row: hit.row, after: hit.after, dx0 };
    }
    case 'badge': case 'cell': case 'columnHeader': case 'rowHeader': case 'wire': return whole();
    default: return unreachable(hit, 'schematic pick');
  }
}

/** What a move of a drag makes. `begin`: the first move past the slop of a live
 *  drag (the app begins its gesture); `edit`: made live, in that gesture;
 *  `shift`: the whole view moved this far from where it was pressed. */
export interface ElecDragStep { begin: boolean; edit?: ElecEdit; shift?: Vec2 }

/** The pointer at `at` (paper mm); null while it is within `slop` of where it
 *  was pressed, so a click — a wobble — makes nothing. `layout`: the view as it
 *  is laid out now — a jog is a fraction of the way between its wire's ends AS
 *  THEY NOW STAND (once its wire has a jog by hand it leaves its corridor's lane
 *  and the rows below move up; a badge may open a pad), so it stays under the
 *  cursor; absent, the ends as they stood at the press. */
export function elecDragMove(d: ElecDrag, view: ElecView, at: Vec2, slop: number, layout?: ElecLayout): ElecDragStep | null {
  if (!d.moved && Math.hypot(at.x - d.grab.x, at.y - d.grab.y) <= slop) return null;
  const begin = !d.moved && LIVE[d.mode];
  d.moved = true;
  d.cur = at;
  const dx = at.x - d.grab.x, dy = at.y - d.grab.y;
  switch (d.mode) {
    case 'aside': return { begin, edit: { kind: 'viewAside', view, which: d.which, at: { x: d.from.x + dx, y: d.from.y + dy } } };
    case 'border': return { begin, edit: { kind: 'border', row: d.row, after: d.after, dx: Math.round((d.dx0 + dx) * 10) / 10 } };
    case 'box': case 'end': return { begin };
    case 'jog': {
      const w = layout?.wires.find((x) => x.key === d.key && !x.offSheet);
      const P = w ? w.points[0] : d.P, Q = w ? w.points[w.points.length - 1] : d.Q;
      return { begin, edit: { kind: 'wire', key: d.key, figures: { jog: (at.y - P.y) / (Q.y - P.y || 1) } } };
    }
    case 'view': return { begin, shift: { x: dx, y: dy } };
    default: return unreachable(d, 'schematic drag');
  }
}

/** What a drop makes — a box moved, a wire's end re-connected — and what is
 *  picked after it; null for a live drag (its moves were its edit), or dropped
 *  where it changes nothing. The app asks the edit's refusal before making it. */
export function elecDragDrop(d: ElecDrag, layout: ElecLayout, model: ElecModel): { edit: ElecEdit; pick: ElecPick } | null {
  if (!d.moved) return null;
  switch (d.mode) {
    case 'box': {
      const t = elecDropTarget(layout, model, d.key, d.cur);
      return t ? { edit: { kind: 'boxMove', key: d.key, cell: t.cell, index: t.index, ...(t.drawn ? { drawn: t.drawn } : {}), ...(t.auto ? { auto: t.auto } : {}) }, pick: { kind: 'box', key: d.key } } : null;
    }
    case 'end': {
      const tb = elecBoxUnder(layout, d.cur);
      return tb && tb.key !== d.box ? { edit: { kind: 'wireEnd', key: d.key, was: d.box, to: tb.key }, pick: { kind: 'wire', key: d.key } } : null;
    }
    case 'aside': case 'border': case 'jog': case 'view': return null;
    default: return unreachable(d, 'schematic drag');
  }
}

/** What a drag under way shows: a box's ghost and the cell it lands in; a wire's
 *  end on its way, at right angles from its other end, outlining only a box the
 *  drop would take (`refusal`: the app's, as the drop asks it). */
export function elecDragShows(d: ElecDrag, layout: ElecLayout, model: ElecModel, refusal: (edit: ElecEdit) => ElecRefusal | null): Pick<ElecDrawState, 'drag' | 'rubber'> {
  if (!d.moved) return {};
  if (d.mode === 'box') return { drag: { key: d.key, dx: d.cur.x - d.grab.x, dy: d.cur.y - d.grab.y, target: elecDropTarget(layout, model, d.key, d.cur)?.rect } };
  if (d.mode !== 'end') return {};
  const tb = elecBoxUnder(layout, d.cur);
  const ok = !!tb && !refusal({ kind: 'wireEnd', key: d.key, was: d.box, to: tb.key });
  return { rubber: { from: d.fixed, to: ok ? elecFacing(tb!.rect, d.fixed) : d.cur, color: d.color, target: ok ? tb!.key : null, dim: d.key } };
}
